import { useAppSelector } from '@/store/hooks';
import { getRSVPLinkSync } from '@/store/slices/integrations/selectors';
import { SessionTypesEnum } from '@/types/session.types';
import {
  getWorkingProjectSessions,
  selectWorkingProjectRegistrationEnabled,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectSocialSettings,
  selectWorkingProjectSettings,
} from '@/store/slices/project/selectors';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import { useMemo } from 'react';
import { RSVPLinkSyncStatus } from '@/types/rsvp.types';
import { PageModuleType, QuestionPrompt } from '@/types/module.types';
import { RegistrationStepType } from '@/types/registration.types';

export default function useComputedRegistrationSettings() {
  const rsvpLinkSync = useAppSelector(getRSVPLinkSync);
  const workingProjectSessions = useAppSelector(getWorkingProjectSessions);
  const workingProjectSocialSettings = useAppSelector(selectWorkingProjectSocialSettings);
  const workingProjectSettings = useAppSelector(selectWorkingProjectSettings);

  const registrationOn = useAppSelector(selectWorkingProjectRegistrationEnabled) || false;
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps) || [];
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const singleSignOnEnabled = registrationSettings?.singleSignOn?.isOn || false;
  const directoryOn = workingProjectSocialSettings?.directory?.isOn;
  const messagingIsOn = workingProjectSocialSettings?.messaging;
  const myListIsOn = workingProjectSettings?.personalizedAttendeeList;
  const courseOn = workingProjectSettings?.course_enabled;
  const leaderboardEnabled = !!workingProjectSettings?.leaderboard_settings?.enabled; // enabled in settings and flag on
  const ticketingOn = registrationSteps?.find((step) => step.type === RegistrationStepType.ticketing)?.isOn;
  const muxDrmEnabled = useCanUseChannelFeature(ChannelFeaturesEnum.mux_drm);
  const rsvpLinkSyncApproved = rsvpLinkSync?.status === RSVPLinkSyncStatus.approved;
  const passwordGateEnabled = registrationSettings?.password_gating_enabled || false;

  const watermarkOn = useMemo(() => {
    return workingProjectSessions?.some((session) => session?.streaming_options?.watermark);
  }, [workingProjectSessions]);

  const containsBreakoutSession = useMemo(() => {
    return workingProjectSessions?.some((session) => session.session_type === SessionTypesEnum.breakoutRooms);
  }, [workingProjectSessions]);

  const containsFiresideSession = useMemo(() => {
    return workingProjectSessions?.some((session) => session.session_type === SessionTypesEnum.fireside);
  }, [workingProjectSessions]);

  const containsPrivateResponses = useMemo(() => {
    return (
      workingProjectSessions?.some((session) =>
        session.modules?.some(
          (module) =>
            module.type === PageModuleType.questions &&
            (module.modules as QuestionPrompt[])?.some((item) => item?.settings?.private_responses)
        )
      ) ?? false
    );
  }, [workingProjectSessions]);

  const containsBreakoutOrFiresideSession = containsBreakoutSession || containsFiresideSession;

  const registrationRequired =
    containsBreakoutOrFiresideSession ||
    directoryOn ||
    messagingIsOn ||
    myListIsOn ||
    leaderboardEnabled ||
    watermarkOn ||
    containsPrivateResponses;

  return {
    registrationOn,
    directoryOn,
    messagingIsOn,
    myListIsOn,
    leaderboardEnabled,
    watermarkOn,
    containsPrivateResponses,
    containsBreakoutOrFiresideSession,
    registrationRequired,
    rsvpLinkSyncApproved,
    muxDrmEnabled,
    courseOn,
    singleSignOnEnabled,
    passwordGateEnabled,
    registrationSteps,
    registrationSettings,
    ticketingOn,
  };
}
